import { Dialog, DialogTitle } from 'components/Dialog';
import { Box } from '@mui/material';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { closeUnlessEscape } from 'utils/closeUnlessEscape';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useId, useState } from 'react';
import { usePolls } from 'features/polls/usePolls';
import { usePollTemplates } from 'features/polls/hooks/usePollTemplates';
import QuizFormContainer from 'features/quizzes/components/quiz-form/QuizFormContainer';
import { editQuizTemplateRequested } from 'features/quizzes/actions';
import type { CreateQuizData } from 'features/quizzes/types';
import { selectQuizTemplateById } from 'features/quizzes/quizzesSlice';

const EditQuizTemplateModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const templateId = modalData.id as string;

  const { isFeatureAvailable } = usePolls();
  const { canManageTemplates } = usePollTemplates();

  const template = useAppSelector((state) => selectQuizTemplateById(state, templateId));

  const [quizData, setQuizData] = useState<CreateQuizData>({
    title: template?.title || '',
    timeLimitSeconds: template?.timeLimitSeconds || 0,
    timingMode: template?.timingMode || 'quiz',
    showCorrectAnswers: template?.showCorrectAnswers || false,
    passingScorePercentage: template?.passingScorePercentage,
    questions:
      template?.questions.map((q) => ({
        text: q.text,
        multipleChoices: q.multipleChoices,
        choices: q.choices.map((c) => ({
          text: c.text,
          correct: c.correct,
        })),
      })) || [],
  });

  const modalShouldBeClosed = !isFeatureAvailable || !canManageTemplates || !template;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('editQuizTemplate'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleFormChange = (data: CreateQuizData) => {
    setQuizData(data);
  };

  const handleSubmit = (data: CreateQuizData) => {
    dispatch(
      editQuizTemplateRequested({
        id: templateId,
        title: data.title,
        timeLimitSeconds: data.timeLimitSeconds,
        timingMode: data.timingMode,
        showCorrectAnswers: data.showCorrectAnswers,
        passingScorePercentage: data.passingScorePercentage,
        questions: data.questions,
      })
    );
    dispatch(closeModal('editQuizTemplate'));
  };

  const handleCancel = () => {
    dispatch(closeModal('editQuizTemplate'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      fullWidth
      aria-labelledby={`${modalId}-title`}
      onClose={closeUnlessEscape(handleCancel)}
      disableRestoreFocus
    >
      <DialogTitle id={`${modalId}-title`}>{t('quizzes.edit_quiz_template.title')}</DialogTitle>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <QuizFormContainer
          quizData={quizData}
          onChange={handleFormChange}
          onDialogClose={handleCancel}
          onDialogSubmit={handleSubmit}
          editForm
        />
      </Box>
    </Dialog>
  );
};

export default EditQuizTemplateModal;
